import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { getOfferLettersReport } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, Download, RefreshCw, Search, IndianRupee,
  CheckCircle, Clock, XCircle, Send, FileCheck
} from 'lucide-react';
import { toast } from 'sonner';

const OfferLettersSummary = ({ user, setUser }) => {
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  // Date filter chips: 'all' | 'today' | 'last7' | 'last30' | 'this_month' | 'last_month' | 'custom'
  const [dateFilter, setDateFilter] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await getOfferLettersReport();
      setReportData(response.data.report || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch offer letter report');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'bg-slate-100', text: 'text-slate-700', icon: <FileText size={12} /> },
      generated: { bg: 'bg-blue-100', text: 'text-blue-700', icon: <FileCheck size={12} /> },
      sent: { bg: 'bg-purple-100', text: 'text-purple-700', icon: <Send size={12} /> },
      accepted: { bg: 'bg-green-100', text: 'text-green-700', icon: <CheckCircle size={12} /> },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: <XCircle size={12} /> },
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: <Clock size={12} /> },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <Badge className={`${config.bg} ${config.text} capitalize gap-1`}>
        {config.icon}
        {status}
      </Badge>
    );
  };

  // Get unique locations for filter
  const uniqueLocations = [...new Set(reportData.map(item => item.work_location).filter(Boolean))];

  /**
   * Compute the inclusive [from, to] date window (YYYY-MM-DD strings) for the
   * active chip. Returns null when no date filter is active.
   */
  const dateRange = (() => {
    const fmt = (d) => d.toISOString().split('T')[0];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateFilter === 'today') {
      const s = fmt(today);
      return { from: s, to: s };
    }
    if (dateFilter === 'last7') {
      const from = new Date(today); from.setDate(from.getDate() - 6);
      return { from: fmt(from), to: fmt(today) };
    }
    if (dateFilter === 'last30') {
      const from = new Date(today); from.setDate(from.getDate() - 29);
      return { from: fmt(from), to: fmt(today) };
    }
    if (dateFilter === 'this_month') {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmt(from), to: fmt(today) };
    }
    if (dateFilter === 'last_month') {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0); // last day of prev month
      return { from: fmt(from), to: fmt(to) };
    }
    if (dateFilter === 'custom' && (customFrom || customTo)) {
      return { from: customFrom || '0000-01-01', to: customTo || '9999-12-31' };
    }
    return null;
  })();

  // Filter data
  const filteredData = reportData.filter(item => {
    const matchesSearch = 
      item.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.candidate_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.offer_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesLocation = locationFilter === 'all' || item.work_location === locationFilter;

    let matchesDate = true;
    if (dateRange) {
      const d = item.date || '';
      matchesDate = d && d >= dateRange.from && d <= dateRange.to;
    }

    return matchesSearch && matchesStatus && matchesLocation && matchesDate;
  });

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Day', 'Candidate Name', 'Candidate Email ID', 'Designation', 'Work Location', 'Status', 'Fixed CTC', 'Variable', 'Total CTC'];
    const rows = filteredData.map(item => [
      item.date,
      item.day,
      item.candidate_name,
      item.candidate_email,
      item.designation,
      item.work_location,
      item.status,
      item.fixed_ctc,
      item.variable,
      item.total_ctc
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `offer_letter_summary_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  // Check access
  const hasAccess = useSectionAccess('/offer-letters-summary');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-96">
          <FileText className="h-16 w-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
          <p className="text-slate-500 mt-2">You don't have permission to view this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="offer-letter-summary">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Offer Letter Summary</h1>
            <p className="text-slate-500 text-sm">View and export all offer letter details</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchReport} variant="outline" className="gap-2">
              <RefreshCw size={16} />
              Refresh
            </Button>
            <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700 gap-2">
              <Download size={16} />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Total Offers</p>
                <p className="text-2xl font-bold text-slate-900">{summary.total_count || summary.total_offers || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Accepted</p>
                <p className="text-2xl font-bold text-green-600">{summary.by_status?.accepted || summary.status_counts?.accepted || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{(summary.by_status?.sent || summary.status_counts?.sent || 0) + (summary.by_status?.pending || summary.status_counts?.pending || 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Total Fixed CTC</p>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(summary.total_fixed_ctc || summary.total_ctc)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Total CTC</p>
                <p className="text-xl font-bold text-indigo-600">{formatCurrency(summary.total_ctc)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4 space-y-3">
            {/* Date filter chips — quick presets for the most common windows */}
            <div className="flex flex-wrap items-center gap-2" data-testid="date-chip-row">
              <span className="text-xs text-slate-500 mr-1">Date:</span>
              {[
                { key: 'all', label: 'All Time' },
                { key: 'today', label: 'Today' },
                { key: 'last7', label: 'Last 7 days' },
                { key: 'last30', label: 'Last 30 days' },
                { key: 'this_month', label: 'This Month' },
                { key: 'last_month', label: 'Last Month' },
                { key: 'custom', label: 'Custom Range' },
              ].map((chip) => {
                const active = dateFilter === chip.key;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setDateFilter(chip.key)}
                    data-testid={`date-chip-${chip.key}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      active
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {chip.label}
                  </button>
                );
              })}
              {dateFilter === 'custom' && (
                <div className="flex items-center gap-2 ml-2" data-testid="custom-date-inputs">
                  <Input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="h-8 w-40"
                    data-testid="custom-date-from"
                  />
                  <span className="text-xs text-slate-500">to</span>
                  <Input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="h-8 w-40"
                    data-testid="custom-date-to"
                  />
                </div>
              )}
              {dateRange && (
                <span className="text-xs text-slate-400 ml-2" data-testid="date-range-summary">
                  {dateRange.from === dateRange.to
                    ? dateRange.from
                    : `${dateRange.from} → ${dateRange.to}`}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-slate-500 mb-1 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, designation..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="w-40">
                <label className="text-xs text-slate-500 mb-1 block">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="generated">Generated</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-48">
                <label className="text-xs text-slate-500 mb-1 block">Work Location</label>
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {uniqueLocations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setLocationFilter('all');
                  setDateFilter('all');
                  setCustomFrom('');
                  setCustomTo('');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              Offer Letters ({filteredData.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p>No offer letters found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1100px]">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left py-3 px-3 font-medium text-slate-600 whitespace-nowrap">Date</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600 whitespace-nowrap">Day</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600 whitespace-nowrap">Candidate Name</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600 whitespace-nowrap">Candidate Email ID</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600 whitespace-nowrap">Designation</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600 whitespace-nowrap">Work Location</th>
                      <th className="text-left py-3 px-3 font-medium text-slate-600 whitespace-nowrap">Status</th>
                      <th className="text-right py-3 px-3 font-medium text-slate-600 whitespace-nowrap">Fixed CTC</th>
                      <th className="text-right py-3 px-3 font-medium text-slate-600 whitespace-nowrap">Variable</th>
                      <th className="text-right py-3 px-3 font-medium text-slate-600 whitespace-nowrap">Total CTC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item, index) => (
                      <tr key={item.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-3 text-slate-900 whitespace-nowrap">{item.date || '-'}</td>
                        <td className="py-3 px-3 text-slate-600 whitespace-nowrap">{item.day || '-'}</td>
                        <td className="py-3 px-3">
                          <span className="font-medium text-slate-900">{item.candidate_name || '-'}</span>
                        </td>
                        <td className="py-3 px-3 text-slate-600">{item.candidate_email || '-'}</td>
                        <td className="py-3 px-3 text-slate-700">{item.designation || '-'}</td>
                        <td className="py-3 px-3 text-slate-600">{item.work_location || '-'}</td>
                        <td className="py-3 px-3 whitespace-nowrap">{getStatusBadge(item.status)}</td>
                        <td className="py-3 px-3 text-right font-medium text-slate-900 whitespace-nowrap">
                          {formatCurrency(item.fixed_ctc)}
                        </td>
                        <td className="py-3 px-3 text-right text-slate-600 whitespace-nowrap">
                          {formatCurrency(item.variable)}
                        </td>
                        <td className="py-3 px-3 text-right font-bold text-indigo-600 whitespace-nowrap">
                          {formatCurrency(item.total_ctc)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Table Footer with Totals */}
                  <tfoot className="bg-slate-100 border-t-2">
                    <tr>
                      <td colSpan="7" className="py-3 px-4 font-bold text-slate-700">
                        Total ({filteredData.length} records)
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-slate-900">
                        {formatCurrency(filteredData.reduce((sum, item) => sum + (item.fixed_ctc || 0), 0))}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-slate-700">
                        {formatCurrency(filteredData.reduce((sum, item) => sum + (item.variable || 0), 0))}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-indigo-700">
                        {formatCurrency(filteredData.reduce((sum, item) => sum + (item.total_ctc || 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default OfferLettersSummary;
